import { CloseRounded } from "@mui/icons-material";
import { Box, Button, Chip, Dialog, Divider, IconButton, Typography } from "@mui/material";
import { useState } from "react";
import { black, greyEnabled, purple, purpleLight } from "../../../../theme/colors";
import {
  RedemptionPayoutStatus,
  RedemptionRequestStatus,
} from "../../model/redemption_request";

export interface RedemptionRequestFilterValues {
  status: RedemptionRequestStatus | null;
  payoutStatus: RedemptionPayoutStatus | null;
}

const STATUS_OPTIONS: RedemptionRequestStatus[] = [
  "Pending",
  "In Progress",
  "Rejected",
  "Completed",
  "Failed",
];

const PAYOUT_STATUS_OPTIONS: RedemptionPayoutStatus[] = [
  "Pending",
  "Processing",
  "Released",
  "Failed",
];

export default function FilterRedemptionRequests({
  open,
  onClose,
  status,
  payoutStatus,
  onApply,
}: RedemptionRequestFilterValues & {
  open: boolean;
  onClose: () => void;
  onApply: (filters: RedemptionRequestFilterValues) => void;
}) {
  const [_status, _setStatus] = useState<RedemptionRequestStatus | null>(status);
  const [_payoutStatus, _setPayoutStatus] = useState<RedemptionPayoutStatus | null>(payoutStatus);

  const handleClose = () => onClose();

  const handleReset = () => {
    onApply({ status: null, payoutStatus: null });
    onClose();
  };

  const handleApply = () => {
    onApply({ status: _status, payoutStatus: _payoutStatus });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} PaperProps={{ sx: { borderRadius: 4 } }}>
      <Box sx={{ width: 400, maxWidth: "90vw" }}>
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ px: 3, pt: 2.5, pb: 1.5, borderBottom: "1px solid #ECECEC" }}
        >
          <Typography variant="h6" fontWeight={700} color={black}>
            Filter Requests
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseRounded sx={{ color: greyEnabled }} />
          </IconButton>
        </Box>

        <Box sx={{ px: 3, pt: 2, pb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="body2" fontWeight={600} color={black}>
              Filter By
            </Typography>
            <Button
              variant="text"
              sx={{ p: 0, minWidth: "auto", color: purple, textTransform: "none", fontWeight: 600 }}
              onClick={handleReset}
            >
              Reset
            </Button>
          </Box>
          <Divider sx={{ mt: 1.5, mb: 2 }} />

          <Typography variant="body2" fontWeight={600} color={black} mb={0.5}>
            Status
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {STATUS_OPTIONS.map((option) => (
              <Chip
                key={option}
                label={option}
                clickable
                variant={_status === option ? "filled" : "outlined"}
                color="primary"
                onClick={() => _setStatus((prev) => (prev === option ? null : option))}
              />
            ))}
          </Box>

          <Typography variant="body2" fontWeight={600} color={black} mb={0.5}>
            Payout Status
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
            {PAYOUT_STATUS_OPTIONS.map((option) => (
              <Chip
                key={option}
                label={option}
                clickable
                variant={_payoutStatus === option ? "filled" : "outlined"}
                color="primary"
                onClick={() => _setPayoutStatus((prev) => (prev === option ? null : option))}
              />
            ))}
          </Box>

          <Box display="flex" gap={1.5} mt={2}>
            <Button
              fullWidth
              onClick={handleClose}
              variant="outlined"
              sx={{
                borderColor: purple,
                color: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { borderColor: purple, bgcolor: purpleLight },
              }}
            >
              Cancel
            </Button>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={handleApply}
              sx={{
                bgcolor: purple,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                "&:hover": { bgcolor: "#7A2074" },
              }}
            >
              Apply Filters
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
}
